import React, { useState, useEffect, useRef } from 'react';
import '../styles/bible.css';
import { getStrongsParser, type StrongsEntry } from '../utils/strongsParser';
import { StrongsPopup } from './StrongsPopup';
import { parseInput, type Reference } from '../bible/reference';
import { mapBookIdToUsfx } from '../bible/reference/bookMapping';
import { parseCurrentUrl, updateUrl, type AppRoute } from '../utils/urlRouter';
import type { CrossReference } from '../bible/crossReferences';
import type { TranslationMeta } from '../bible/translationInfo';
import { ReadingPlanCalendar } from './ReadingPlanCalendar';

interface Verse {
    id: string;
    number: number;
    text: string;
    words: Array<{ strongsId: string; text: string }>;
}

interface Section {
    type: 'heading' | 'paragraph';
    style?: string;
    content: string;
}

interface Chapter {
    id: string;
    number: number;
    sections: Array<Section | Verse>;
}

interface Book {
    id: string;
    name: string;
    chapterCount: number;
}

interface ApiVerse {
    reference: string;
    bookId: string;
    bookName: string;
    chapter: number;
    verse: number;
    text: string;
    words: Array<{ strongsId: string; text: string }>;
    relevance?: string; // For topic search results
}

interface SearchResponse {
    verses: ApiVerse[];
    searchType?: 'reference' | 'topic';
    query?: string;
}

export function BibleReader() {
    // Initialize state from URL
    const initialRoute = parseCurrentUrl();

    const [books, setBooks] = useState<Book[]>([]);
    const [selectedBook, setSelectedBook] = useState<string>(
        initialRoute.bookId || 'GEN',
    );
    const [selectedChapter, setSelectedChapter] = useState<number>(
        initialRoute.chapter || 1,
    );
    const [selectedTranslation, setSelectedTranslation] = useState<string>(
        initialRoute.translation || localStorage.getItem('bible-translation') || 'NET',
    );
    const [chapterData, setChapterData] = useState<Chapter | null>(null);
    const [bookInfo, setBookInfo] = useState<Book | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [strongsPopup, setStrongsPopup] = useState<{
        entry: StrongsEntry;
        position: { x: number; y: number };
    } | null>(null);
    const [strongsLoaded, setStrongsLoaded] = useState(false);

    // Reference search state
    const [searchQuery, setSearchQuery] = useState<string>(
        initialRoute.searchQuery || '',
    );
    const [searchResults, setSearchResults] = useState<ApiVerse[]>([]);
    const [showSearchResults, setShowSearchResults] = useState(
        initialRoute.type === 'search',
    );
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchType, setSearchType] = useState<'reference' | 'topic'>(
        'reference',
    );
    const [highlightVerse, setHighlightVerse] = useState<number | undefined>(
        initialRoute.verse,
    );
    // Reading plan calendar state
    const [showReadingPlan, setShowReadingPlan] = useState(
        initialRoute.type === 'reading-plan',
    );
    const [selectedPlanId, setSelectedPlanId] = useState<string | null>(
        initialRoute.planId || null,
    );
    const [selectedPlanName, setSelectedPlanName] = useState<string>('');

    const scrollRef = useRef<HTMLElement>(null);
    const navRef = useRef<HTMLElement>(null);
    const isInitialLoad = useRef(true);

    // Settings state (initialized from localStorage)
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [fontFamily, setFontFamily] = useState(() => localStorage.getItem('bible-font-family') || 'Arsenal');
    const [fontSize, setFontSize] = useState(() => {
        const stored = localStorage.getItem('bible-font-size');
        return stored ? parseFloat(stored) : 1.125;
    });
    const [textWidth, setTextWidth] = useState(() => {
        const stored = localStorage.getItem('bible-text-width');
        return stored ? parseInt(stored, 10) : 50;
    });
    const settingsRef = useRef<HTMLDivElement>(null);

    // Cross-reference state
    const [xrefCounts, setXrefCounts] = useState<Record<number, number>>({});
    const [xrefPopup, setXrefPopup] = useState<{
        refs: CrossReference[];
        position: { x: number; y: number };
        verseRef: string;
    } | null>(null);

    // Translation info state
    const [translationInfo, setTranslationInfo] = useState<TranslationMeta | null>(null);

    // Apply font settings via CSS custom properties and persist to localStorage
    useEffect(() => {
        const fallback = fontFamily === 'Arsenal' ? 'Georgia, serif' : 'sans-serif';
        document.documentElement.style.setProperty('--font-body', `'${fontFamily}', ${fallback}`);
        document.documentElement.style.setProperty('--font-size-body', `${fontSize}rem`);
        localStorage.setItem('bible-font-family', fontFamily);
        localStorage.setItem('bible-font-size', fontSize.toString());
    }, [fontFamily, fontSize]);

    // Apply text width setting
    useEffect(() => {
        document.documentElement.style.setProperty('--max-width', `${textWidth}%`);
        localStorage.setItem('bible-text-width', textWidth.toString());
    }, [textWidth]);

    // Persist translation preference to localStorage
    useEffect(() => {
        localStorage.setItem('bible-translation', selectedTranslation);
    }, [selectedTranslation]);

    // Close settings dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
                setSettingsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Dynamically adjust content padding to match nav height
    useEffect(() => {
        const nav = navRef.current;
        if (!nav) return;
        const update = () => {
            const h = nav.getBoundingClientRect().height;
            document.documentElement.style.setProperty('--nav-height', `${h}px`);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(nav);
        return () => ro.disconnect();
    }, []);

    // Handle browser navigation (back/forward buttons)
    useEffect(() => {
        const handlePopState = () => {
            const route = parseCurrentUrl();

            if (route.type === 'reading-plan' && route.planId) {
                setSelectedPlanId(route.planId);
                setShowReadingPlan(true);
                setShowSearchResults(false);
            } else if (route.type === 'search' && route.searchQuery) {
                setSearchQuery(route.searchQuery);
                setSelectedTranslation(route.translation || 'NET');
                setShowSearchResults(true);
                setShowReadingPlan(false);
                // Trigger search
                performSearch(route.searchQuery);
            } else {
                // Reader mode
                setShowSearchResults(false);
                setShowReadingPlan(false);
                setSelectedPlanId(null);
                setSearchResults([]);
                setSelectedBook(route.bookId || 'GEN');
                setSelectedChapter(route.chapter || 1);
                setSelectedTranslation(route.translation || 'NET');
                setHighlightVerse(route.verse);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // Handle initial search from URL
    useEffect(() => {
        if (
            isInitialLoad.current &&
            initialRoute.type === 'search' &&
            initialRoute.searchQuery
        ) {
            performSearch(initialRoute.searchQuery);
            isInitialLoad.current = false;
        }
    }, []);

    // Load Strong's lexicon data on mount
    useEffect(() => {
        const parser = getStrongsParser();
        parser
            .load()
            .then(() => {
                setStrongsLoaded(true);
                console.log("Strong's lexicon loaded successfully");
            })
            .catch((err) => {
                console.error("Failed to load Strong's lexicon:", err);
            });
    }, []);

    // Load books list when translation changes
    useEffect(() => {
        fetch(`/api/books?translation=${selectedTranslation}`)
            .then((res) => res.json())
            .then((data) => {
                setBooks(data.books);
                setLoading(false);
            })
            .catch((err) => {
                setError('Failed to load books');
                setLoading(false);
            });
    }, [selectedTranslation]);

    // Load book info when book or translation changes
    useEffect(() => {
        if (!selectedBook) return;

        fetch(`/api/books/${selectedBook}?translation=${selectedTranslation}`)
            .then((res) => res.json())
            .then((data) => {
                setBookInfo(data);
            })
            .catch((err) => {
                console.error('Failed to load book info:', err);
            });
    }, [selectedBook, selectedTranslation]);

    // Update URL when book/chapter changes (but not on initial load)
    useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false;
            return;
        }

        if (!showSearchResults && !showReadingPlan && selectedBook && selectedChapter) {
            updateUrl({
                type: 'reader',
                bookId: selectedBook,
                chapter: selectedChapter,
                verse: highlightVerse,
                translation: selectedTranslation,
            });
        }
    }, [selectedBook, selectedChapter, highlightVerse, showSearchResults, showReadingPlan]);

    // Load chapter data when book, chapter, or translation changes
    useEffect(() => {
        if (!selectedBook || !selectedChapter) return;

        setLoading(true);
        fetch(
            `/api/books/${selectedBook}/${selectedChapter}?translation=${selectedTranslation}`,
        )
            .then((res) => res.json())
            .then((data) => {
                setChapterData(data.chapter);
                setLoading(false);

                // Handle verse highlighting after chapter loads
                if (highlightVerse) {
                    setTimeout(() => {
                        const verseElement = document.querySelector(
                            `[data-verse="${highlightVerse}"]`,
                        );
                        if (verseElement) {
                            verseElement.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                            });
                            verseElement.classList.add('highlighted-verse');
                            // Remove highlight after 3 seconds
                            setTimeout(
                                () =>
                                    verseElement.classList.remove(
                                        'highlighted-verse',
                                    ),
                                3000,
                            );
                        }
                    }, 100);
                }
            })
            .catch((err) => {
                setError('Failed to load chapter');
                setLoading(false);
            });
    }, [selectedBook, selectedChapter, selectedTranslation, highlightVerse]);

    // Load cross-reference counts when book/chapter changes
    useEffect(() => {
        if (!selectedBook || !selectedChapter) return;
        fetch(`/api/crossrefs/${selectedBook}/${selectedChapter}`)
            .then((res) => res.json())
            .then((data) => setXrefCounts(data.counts || {}))
            .catch(() => setXrefCounts({}));
    }, [selectedBook, selectedChapter]);

    // Load translation info when translation changes
    useEffect(() => {
        fetch(`/api/translation/${selectedTranslation}`)
            .then((res) => res.json())
            .then((data) => setTranslationInfo(data.translation || null))
            .catch(() => setTranslationInfo(null));
    }, [selectedTranslation]);

    const handleXrefClick = async (bookId: string, chapter: number, verse: number, event: React.MouseEvent) => {
        event.stopPropagation();
        try {
            const res = await fetch(`/api/crossrefs/${bookId}/${chapter}/${verse}`);
            const data = await res.json();
            setXrefPopup({
                refs: data.references || [],
                position: { x: event.clientX, y: event.clientY },
                verseRef: `${bookId}.${chapter}.${verse}`,
            });
        } catch {
            // ignore
        }
    };

    const handleXrefNavigate = (bookId: string, chapter: number, verse: number) => {
        setXrefPopup(null);
        setSelectedBook(bookId);
        setSelectedChapter(chapter);
        setHighlightVerse(verse);
        setShowSearchResults(false);
        updateUrl({ type: 'reader', bookId, chapter, verse, translation: selectedTranslation });
    };

    const handleBookChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedBook(e.target.value);
        setSelectedChapter(1); // Reset to chapter 1 when book changes
        setHighlightVerse(undefined); // Clear any verse highlighting
    };

    const handleChapterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedChapter(parseInt(e.target.value, 10));
        setHighlightVerse(undefined); // Clear any verse highlighting
    };

    const handleTranslationChange = (
        e: React.ChangeEvent<HTMLSelectElement>,
    ) => {
        const newTranslation = e.target.value;
        setSelectedTranslation(newTranslation);
        // Reset to chapter 1 when switching translations to be safe
        setSelectedChapter(1);
        setHighlightVerse(undefined);

        // Update URL with new translation
        if (!showSearchResults) {
            updateUrl({
                type: 'reader',
                bookId: selectedBook,
                chapter: 1,
                translation: newTranslation,
            });
        }
    };

    const goToPreviousChapter = () => {
        if (selectedChapter > 1) {
            setSelectedChapter(selectedChapter - 1);
        } else {
            // Go to previous book's last chapter
            const currentBookIndex = books.findIndex(
                (b) => b.id === selectedBook,
            );
            if (currentBookIndex > 0) {
                const previousBook = books[currentBookIndex - 1];
                setSelectedBook(previousBook.id);
                setSelectedChapter(previousBook.chapterCount);
            }
        }
        setHighlightVerse(undefined);
    };

    const goToNextChapter = () => {
        if (bookInfo && selectedChapter < bookInfo.chapterCount) {
            setSelectedChapter(selectedChapter + 1);
        } else {
            // Go to next book's chapter 1
            const currentBookIndex = books.findIndex(
                (b) => b.id === selectedBook,
            );
            if (currentBookIndex < books.length - 1) {
                const nextBook = books[currentBookIndex + 1];
                setSelectedBook(nextBook.id);
                setSelectedChapter(1);
            }
        }
        setHighlightVerse(undefined);
    };

    const isFirstChapter =
        selectedBook === books[0]?.id && selectedChapter === 1;
    const isLastChapter =
        selectedBook === books[books.length - 1]?.id &&
        bookInfo &&
        selectedChapter === bookInfo.chapterCount;

    const handleStrongsClick = (
        strongsNum: string,
        event: React.MouseEvent,
    ) => {
        if (!strongsLoaded) return;

        const parser = getStrongsParser();
        const entry = parser.lookup(strongsNum);

        if (entry) {
            setStrongsPopup({
                entry,
                position: { x: event.clientX, y: event.clientY },
            });
        }
    };

    const handleClosePopup = () => {
        setStrongsPopup(null);
    };

    // Reusable search function
    const performSearch = async (query: string) => {
        if (!query.trim()) return;

        setSearchLoading(true);
        setError(null);

        try {
            // First, try to parse as Bible references
            const references = parseInput(query);
            const hasValidReferences =
                references.length > 0 && references.some((ref) => ref.book > 0);

            if (hasValidReferences) {
                // Handle as Bible reference search
                setSearchType('reference');

                // Check if it's a single reference within one chapter
                const singleChapter = references.every(
                    (ref) =>
                        ref.book === references[0].book &&
                        ref.chapter === references[0].chapter,
                );

                // Check if the verses form a contiguous range (e.g., John 3:16-20)
                const isContiguousRange =
                    singleChapter &&
                    (() => {
                        if (references.length <= 1) return true;
                        const sorted = references
                            .map((r) => r.verse)
                            .sort((a, b) => a - b);
                        for (let i = 1; i < sorted.length; i++) {
                            if (sorted[i]! !== sorted[i - 1]! + 1) return false;
                        }
                        return true;
                    })();

                if (singleChapter && isContiguousRange) {
                    // Single chapter behavior - navigate to that chapter and highlight verse
                    const firstRef = references[0];
                    const usfxBookId = mapBookIdToUsfx(firstRef.book);

                    if (usfxBookId) {
                        setSelectedBook(usfxBookId);
                        setSelectedChapter(firstRef.chapter);
                        setHighlightVerse(firstRef.verse);
                        setShowSearchResults(false);

                        // Update URL for single verse reference
                        updateUrl({
                            type: 'reader',
                            bookId: usfxBookId,
                            chapter: firstRef.chapter,
                            verse: firstRef.verse,
                            translation: selectedTranslation,
                        });
                    }
                } else {
                    // Multi-reference behavior - fetch verses and show results view
                    const response = await fetch(
                        `/api/verses?q=${encodeURIComponent(query)}&translation=${selectedTranslation}`,
                    );
                    const data = await response.json();

                    if (!response.ok) {
                        setError(data.error || 'Failed to fetch verses');
                        setSearchLoading(false);
                        return;
                    }

                    setSearchResults(data.verses);
                    setShowSearchResults(true);

                    // Update URL for multi-reference search
                    updateUrl({
                        type: 'search',
                        searchQuery: query,
                        translation: selectedTranslation,
                    });
                }
            } else {
                // Handle as topic/plain text search using OpenAI
                setSearchType('topic');

                const response = await fetch(
                    `/api/search?q=${encodeURIComponent(query)}&translation=${selectedTranslation}`,
                );
                const data: SearchResponse = await response.json();

                if (!response.ok) {
                    setError(data.error || 'Failed to search for verses');
                    setSearchLoading(false);
                    return;
                }

                setSearchResults(data.verses);
                setShowSearchResults(true);

                // Update URL for topic search
                updateUrl({
                    type: 'search',
                    searchQuery: query,
                    translation: selectedTranslation,
                });
            }
        } catch (error) {
            console.error('Search error:', error);
            setError('Failed to perform search');
        }

        setSearchLoading(false);
    };

    const handleSearchSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await performSearch(searchQuery);
    };

    const handleBackToReader = () => {
        setShowSearchResults(false);
        setShowReadingPlan(false);
        setSelectedPlanId(null);
        setSearchResults([]);
        setSearchQuery('');
        setHighlightVerse(undefined);

        // Update URL to current reader state
        updateUrl({
            type: 'reader',
            bookId: selectedBook,
            chapter: selectedChapter,
            translation: selectedTranslation,
        });
    };

    if (error) {
        return <div className="bible-reader">Error: {error}</div>;
    }

    return (
        <>
            <nav className="bible-nav" ref={navRef}>
                <select
                    value={selectedTranslation}
                    onChange={handleTranslationChange}
                    className="bible-nav-translation"
                >
                    <option value="NET">NET</option>
                    <option value="KJV">KJV</option>
                    <option value="ASV">ASV</option>
                    <option value="GNV">GNV (Geneva 1599)</option>
                    <option value="BSB">BSB (Berean)</option>
                    <option value="WEB">WEB</option>
                </select>

                <form
                    onSubmit={handleSearchSubmit}
                    className="bible-search-form"
                >
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="John 3:16, love your neighbor, prayer..."
                        className="bible-search-input"
                        disabled={searchLoading}
                    />
                    <button
                        type="submit"
                        disabled={searchLoading || !searchQuery.trim()}
                    >
                        {searchLoading ? '...' : 'Go'}
                    </button>
                </form>

                {!showSearchResults && !showReadingPlan && (
                    <>
                        <select
                            value={selectedBook}
                            onChange={handleBookChange}
                        >
                            {books.map((book) => (
                                <option key={book.id} value={book.id}>
                                    {book.name}
                                </option>
                            ))}
                        </select>

                        <select
                            value={selectedChapter}
                            onChange={handleChapterChange}
                        >
                            {bookInfo &&
                                Array.from(
                                    { length: bookInfo.chapterCount },
                                    (_, i) => i + 1,
                                ).map((ch) => (
                                    <option key={ch} value={ch}>
                                        Chapter {ch}
                                    </option>
                                ))}
                        </select>

                        <button
                            onClick={goToPreviousChapter}
                            disabled={isFirstChapter}
                        >
                            ← Previous
                        </button>

                        <button
                            onClick={goToNextChapter}
                            disabled={isLastChapter}
                        >
                            Next →
                        </button>
                    </>
                )}

                {(showSearchResults || showReadingPlan) && (
                    <button
                        onClick={handleBackToReader}
                        className="bible-back-button"
                    >
                        ← Back to Reader
                    </button>
                )}

                <ReadingPlansDropdown
                    onNavigate={(bookId, chapter) => {
                        setSelectedBook(bookId);
                        setSelectedChapter(chapter);
                        setHighlightVerse(undefined);
                        setShowSearchResults(false);
                        setShowReadingPlan(false);
                        updateUrl({ type: 'reader', bookId, chapter, translation: selectedTranslation });
                    }}
                    onShowPlan={(planId, planName) => {
                        setSelectedPlanId(planId);
                        setSelectedPlanName(planName);
                        setShowReadingPlan(true);
                        setShowSearchResults(false);
                        updateUrl({ type: 'reading-plan', planId });
                    }}
                />

                <div className="settings-container" ref={settingsRef}>
                    <button
                        className="settings-gear-btn"
                        onClick={() => setSettingsOpen(!settingsOpen)}
                        title="Settings"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M11.078 0l.212 1.996c.584.16 1.136.392 1.644.692l1.612-1.18 1.52 1.52-1.18 1.612c.3.508.532 1.06.692 1.644L17.574 6.5v2.148l-1.996.212c-.16.584-.392 1.136-.692 1.644l1.18 1.612-1.52 1.52-1.612-1.18c-.508.3-1.06.532-1.644.692l-.212 1.996H8.93l-.212-1.996a5.958 5.958 0 01-1.644-.692l-1.612 1.18-1.52-1.52 1.18-1.612a5.958 5.958 0 01-.692-1.644L2.434 8.648V6.5l1.996-.212c.16-.584.392-1.136.692-1.644l-1.18-1.612 1.52-1.52 1.612 1.18c.508-.3 1.06-.532 1.644-.692L8.93 0h2.148zM10.004 5a2.574 2.574 0 100 5.148 2.574 2.574 0 000-5.148z" transform="translate(0 2.5)"/>
                        </svg>
                    </button>
                    {settingsOpen && (
                        <div className="settings-dropdown">
                            <div className="settings-row">
                                <label>Font</label>
                                <select
                                    value={fontFamily}
                                    onChange={(e) => setFontFamily(e.target.value)}
                                    className="settings-select"
                                >
                                    <option value="Arsenal">Arsenal</option>
                                    <option value="Inter">Inter</option>
                                    <option value="Avenir">Avenir</option>
                                    <option value="Helvetica">Helvetica</option>
                                    <option value="Arial">Arial</option>
                                </select>
                            </div>
                            <div className="settings-row">
                                <label>Size</label>
                                <div className="settings-font-size-controls">
                                    <button
                                        onClick={() => setFontSize(s => Math.max(0.75, +(s - 0.125).toFixed(3)))}
                                        disabled={fontSize <= 0.75}
                                    >
                                        −
                                    </button>
                                    <span className="settings-font-size-display">
                                        {Math.round(fontSize * 16)}px
                                    </span>
                                    <button
                                        onClick={() => setFontSize(s => Math.min(2, +(s + 0.125).toFixed(3)))}
                                        disabled={fontSize >= 2}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                            <div className="settings-row">
                                <label>Width</label>
                                <div className="settings-font-size-controls">
                                    <button
                                        onClick={() => setTextWidth(w => Math.max(40, w - 10))}
                                        disabled={textWidth <= 40}
                                    >
                                        −
                                    </button>
                                    <span className="settings-font-size-display">
                                        {textWidth}%
                                    </span>
                                    <button
                                        onClick={() => setTextWidth(w => Math.min(100, w + 10))}
                                        disabled={textWidth >= 100}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </nav>

            <main className="bible-reader" ref={scrollRef}>
                {showReadingPlan && selectedPlanId ? (
                    <ReadingPlanCalendar
                        planId={selectedPlanId}
                        planName={selectedPlanName}
                        onNavigate={(bookId, chapter) => {
                            setSelectedBook(bookId);
                            setSelectedChapter(chapter);
                            setHighlightVerse(undefined);
                            setShowReadingPlan(false);
                            setShowSearchResults(false);
                            updateUrl({ type: 'reader', bookId, chapter, translation: selectedTranslation });
                        }}
                    />
                ) : showSearchResults ? (
                    <>
                        <SearchResults
                            results={searchResults}
                            query={searchQuery}
                            searchType={searchType}
                            onStrongsClick={handleStrongsClick}
                            strongsLoaded={strongsLoaded}
                        />
                        {translationInfo && <TranslationFooter info={translationInfo} />}
                    </>
                ) : loading ? (
                    <div>Loading...</div>
                ) : chapterData ? (
                    <>
                        <ChapterDisplay
                            bookName={bookInfo?.name || selectedBook}
                            bookId={selectedBook}
                            chapterNumber={selectedChapter}
                            chapter={chapterData}
                            onStrongsClick={handleStrongsClick}
                            strongsLoaded={strongsLoaded}
                            xrefCounts={xrefCounts}
                            onXrefClick={handleXrefClick}
                        />
                        {translationInfo && <TranslationFooter info={translationInfo} />}
                    </>
                ) : (
                    <div>No chapter data available</div>
                )}
            </main>

            {strongsPopup && (
                <StrongsPopup
                    entry={strongsPopup.entry}
                    position={strongsPopup.position}
                    onClose={handleClosePopup}
                />
            )}

            {xrefPopup && (
                <CrossRefPopup
                    refs={xrefPopup.refs}
                    position={xrefPopup.position}
                    onClose={() => setXrefPopup(null)}
                    onNavigate={handleXrefNavigate}
                />
            )}
        </>
    );
}

function groupContiguousVerses(verses: ApiVerse[]): ApiVerse[][] {
    if (verses.length === 0) return [];
    const groups: ApiVerse[][] = [];
    let current: ApiVerse[] = [verses[0]!];

    for (let i = 1; i < verses.length; i++) {
        const prev = verses[i - 1]!;
        const curr = verses[i]!;
        if (
            curr.bookId === prev.bookId &&
            curr.chapter === prev.chapter &&
            curr.verse === prev.verse + 1
        ) {
            current.push(curr);
        } else {
            groups.push(current);
            current = [curr];
        }
    }
    groups.push(current);
    return groups;
}

interface SearchResultsProps {
    results: ApiVerse[];
    query: string;
    searchType: 'reference' | 'topic';
    onStrongsClick: (strongsNum: string, event: React.MouseEvent) => void;
    strongsLoaded: boolean;
}

function SearchResults({
    results,
    query,
    searchType,
    onStrongsClick,
    strongsLoaded,
}: SearchResultsProps) {
    if (results.length === 0) {
        return (
            <article>
                <header>
                    <h1 className="bible-book-title">Search Results</h1>
                    <p>No verses found for "{query}"</p>
                    {/* <div className="search-type-indicator">
            <small>{searchType === 'reference' ? 'Reference Lookup' : 'Topic Search'}</small>
          </div> */}
                </header>
            </article>
        );
    }

    return (
        <article>
            <header>
                <h1 className="bible-book-title">Search Results</h1>
                <p>
                    {results.length} verse{results.length !== 1 ? 's' : ''} for
                    "{query}"
                </p>
                {/* <div className="search-type-indicator">
          <small>{searchType === 'reference' ? 'Reference Lookup' : 'Topic Search'}</small>
        </div> */}
            </header>

            <div className="search-results">
                {groupContiguousVerses(results).map((group, groupIndex) => {
                    const first = group[0]!;
                    const last = group[group.length - 1]!;
                    const label =
                        group.length === 1
                            ? first.reference
                            : `${first.bookName} ${first.chapter}:${first.verse}-${last.verse}`;

                    return (
                        <div
                            key={`group-${groupIndex}`}
                            className="search-result-verse"
                        >
                            <h3 className="search-result-reference">
                                <a
                                    href={`/${first.bookId}/${first.chapter}?v=${first.verse}`}
                                    className="search-result-reference-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        window.location.href = `/${first.bookId}/${first.chapter}?v=${first.verse}`;
                                    }}
                                >
                                    {label}
                                </a>
                            </h3>
                            <p className="bible-paragraph">
                                {group.map((verse) => (
                                    <span
                                        key={`${verse.bookId}-${verse.chapter}-${verse.verse}`}
                                        className="bible-verse"
                                    >
                                        <sup className="bible-verse-number">
                                            {verse.verse}
                                        </sup>
                                        <span className="bible-verse-text">
                                            {renderVerseText(
                                                {
                                                    id: `${verse.bookId}-${verse.chapter}-${verse.verse}`,
                                                    number: verse.verse,
                                                    text: verse.text,
                                                    words: verse.words,
                                                },
                                                onStrongsClick,
                                                strongsLoaded,
                                            )}
                                        </span>
                                    </span>
                                ))}
                            </p>
                            {first.relevance && (
                                <p className="search-result-relevance">
                                    <small>
                                        <em>{first.relevance}</em>
                                    </small>
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </article>
    );
}

interface ChapterDisplayProps {
    bookName: string;
    bookId: string;
    chapterNumber: number;
    chapter: Chapter;
    onStrongsClick: (strongsNum: string, event: React.MouseEvent) => void;
    strongsLoaded: boolean;
    xrefCounts: Record<number, number>;
    onXrefClick: (bookId: string, chapter: number, verse: number, event: React.MouseEvent) => void;
}

function ChapterDisplay({
    bookName,
    bookId,
    chapterNumber,
    chapter,
    onStrongsClick,
    strongsLoaded,
    xrefCounts,
    onXrefClick,
}: ChapterDisplayProps) {
    return (
        <article>
            <header>
                <h1 className="bible-book-title">{bookName}</h1>
            </header>

            {renderSections(
                chapter.sections,
                chapterNumber,
                onStrongsClick,
                strongsLoaded,
                xrefCounts,
                bookId,
                onXrefClick,
            )}
        </article>
    );
}

function renderSections(
    sections: Array<Section | Verse>,
    chapterNumber: number,
    onStrongsClick: (strongsNum: string, event: React.MouseEvent) => void,
    strongsLoaded: boolean,
    xrefCounts: Record<number, number> = {},
    bookId?: string,
    onXrefClick?: (bookId: string, chapter: number, verse: number, event: React.MouseEvent) => void,
) {
    const elements: JSX.Element[] = [];
    let currentParagraph: Verse[] = [];
    let paragraphKey = 0;
    let isFirstParagraph = true;

    const flushParagraph = () => {
        if (currentParagraph.length > 0) {
            const includeChapterNumber = isFirstParagraph;
            elements.push(
                <p key={`para-${paragraphKey++}`} className="bible-paragraph">
                    {includeChapterNumber && (
                        <span className="bible-chapter-number-wrapper">
                            <span className="bible-chapter-number">
                                {chapterNumber}
                            </span>
                        </span>
                    )}
                    {currentParagraph.map((verse, idx) => (
                        <React.Fragment key={verse.id}>
                            <span
                                className="bible-verse"
                                data-verse={verse.number}
                            >
                                <sup className="bible-verse-number">
                                    {verse.number}
                                </sup>
                                <span className="bible-verse-text">
                                    {renderVerseText(
                                        verse,
                                        onStrongsClick,
                                        strongsLoaded,
                                    )}
                                </span>
                                {xrefCounts[verse.number] && bookId && onXrefClick && (
                                    <sup
                                        className="xref-marker"
                                        onClick={(e) => onXrefClick(bookId, chapterNumber, verse.number, e)}
                                        title={`${xrefCounts[verse.number]} cross-references`}
                                    >
                                        †{xrefCounts[verse.number]}
                                    </sup>
                                )}
                            </span>
                            {idx < currentParagraph.length - 1 && ' '}
                        </React.Fragment>
                    ))}
                </p>,
            );
            currentParagraph = [];
            isFirstParagraph = false;
        }
    };

    sections.forEach((section, idx) => {
        if ('type' in section) {
            // It's a Section (heading)
            flushParagraph();
            elements.push(
                <h3
                    key={`section-${idx}`}
                    className={`bible-heading ${section.style || 's1'}`}
                >
                    {section.content}
                </h3>,
            );
        } else {
            // It's a Verse
            currentParagraph.push(section);
        }
    });

    // Flush any remaining verses
    flushParagraph();

    return elements;
}

/**
 * Render verse text with clickable Strong's numbers
 */
function renderVerseText(
    verse: Verse,
    onStrongsClick: (strongsNum: string, event: React.MouseEvent) => void,
    strongsLoaded: boolean,
): React.ReactNode {
    // If no words with Strong's numbers, just return the plain text
    if (!verse.words || verse.words.length === 0) {
        return verse.text;
    }

    // Render each word as a clickable element if it has a Strong's number
    return verse.words.map((word, idx) => {
        if (word.strongsId && strongsLoaded) {
            return (
                <span
                    key={`${verse.id}-word-${idx}`}
                    className="strongs-word"
                    onClick={(e) => onStrongsClick(word.strongsId, e)}
                    data-strongs={word.strongsId}
                >
                    {word.text}
                </span>
            );
        } else {
            return (
                <React.Fragment key={`${verse.id}-word-${idx}`}>
                    {word.text}{' '}
                </React.Fragment>
            );
        }
    });
}

/**
 * Cross-Reference Popup
 */
interface CrossRefPopupProps {
    refs: CrossReference[];
    position: { x: number; y: number };
    onClose: () => void;
    onNavigate: (bookId: string, chapter: number, verse: number) => void;
}

function CrossRefPopup({ refs, position, onClose, onNavigate }: CrossRefPopupProps) {
    const popupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    useEffect(() => {
        if (!popupRef.current) return;
        const popup = popupRef.current;
        const rect = popup.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        let x = position.x;
        let y = position.y + 10;
        if (x + rect.width > vw - 20) x = vw - rect.width - 20;
        if (y + rect.height > vh - 20) y = position.y - rect.height - 10;
        if (x < 20) x = 20;
        if (y < 20) y = 20;

        popup.style.left = `${x}px`;
        popup.style.top = `${y}px`;
    }, [position]);

    return (
        <>
            <div className="strongs-popup-overlay" onClick={onClose} />
            <div ref={popupRef} className="xref-popup">
                <div className="xref-popup-header">
                    <span className="xref-popup-title">Cross References ({refs.length})</span>
                    <button className="strongs-popup-close" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="xref-popup-list">
                    {refs.slice(0, 25).map((ref, i) => {
                        const label = ref.endVerse
                            ? `${ref.bookName} ${ref.chapter}:${ref.verse}–${ref.endVerse}`
                            : `${ref.bookName} ${ref.chapter}:${ref.verse}`;
                        return (
                            <a
                                key={i}
                                className="xref-popup-link"
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    onNavigate(ref.bookId, ref.chapter, ref.verse);
                                }}
                            >
                                {label}
                            </a>
                        );
                    })}
                    {refs.length > 25 && (
                        <span className="xref-popup-more">and {refs.length - 25} more…</span>
                    )}
                </div>
            </div>
        </>
    );
}

/**
 * Reading Plans Dropdown
 */
interface PlanData {
    id: string;
    name: string;
    today: {
        display: string;
        references: Array<{ text: string; bookId: string; chapter: number }>;
        noReading?: boolean;
    };
}

function ReadingPlansDropdown({ onNavigate, onShowPlan }: {
    onNavigate: (bookId: string, chapter: number) => void;
    onShowPlan: (planId: string, planName: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const [plans, setPlans] = useState<PlanData[]>([]);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetch('/api/reading-plans')
            .then(r => r.json())
            .then(data => setPlans(data.plans || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div className="reading-plans-container" ref={ref}>
            <button
                className="settings-gear-btn"
                onClick={() => setOpen(!open)}
                title="Reading Plans"
            >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M6 1a1 1 0 0 1 1 1v1h6V2a1 1 0 1 1 2 0v1h1.5A2.5 2.5 0 0 1 19 5.5v11a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 1 16.5v-11A2.5 2.5 0 0 1 3.5 3H5V2a1 1 0 0 1 1-1zM3 8v8.5a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5V8H3z"/>
                </svg>
            </button>
            {open && (
                <div className="reading-plans-dropdown">
                    <div className="reading-plans-title">Today's Reading</div>
                    {plans.map(plan => (
                        <div key={plan.id} className="reading-plan-row">
                            <a
                                href={`/plan/${plan.id}`}
                                className="reading-plan-name reading-plan-name-link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    onShowPlan(plan.id, plan.name);
                                    setOpen(false);
                                }}
                            >{plan.name}</a>
                            <span className="reading-plan-sep"> — </span>
                            {plan.today.noReading ? (
                                <span className="reading-plan-no-reading">No reading today</span>
                            ) : (
                                <span className="reading-plan-refs">
                                    {plan.today.references.map((r, i) => (
                                        <span key={i}>
                                            {i > 0 && '; '}
                                            <a
                                                href="#"
                                                className="reading-plan-link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    onNavigate(r.bookId, r.chapter);
                                                    setOpen(false);
                                                }}
                                            >
                                                {r.text}
                                            </a>
                                        </span>
                                    ))}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Translation Footer
 */
function TranslationFooter({ info }: { info: TranslationMeta }) {
    return (
        <footer className="translation-footer">
            <div className="translation-footer-name">{info.fullName} ({info.id})</div>
            <div className="translation-footer-desc">{info.description}</div>
            <div className="translation-footer-copyright">{info.copyright}</div>
            <a href={info.website} target="_blank" rel="noopener noreferrer" className="translation-footer-link">
                {info.website}
            </a>
        </footer>
    );
}
